/* Giveaway Ninja is the authority for configured product and order bonuses.
 * API contract: https://www.giveaway.ninja/support/api
 * All amounts are cents. Products use unit prices; orders use line totals.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory(root);
  else root.MNGiveawayNinja = factory(root);
})(typeof globalThis !== 'undefined' ? globalThis : this, function (root) {
  'use strict';

  var BASE = 'https://api.giveaway.ninja/api/client/';
  var CACHE_LIMIT = 256;
  var BATCH_LIMIT = 100;
  var REQUEST_LIMIT = 12; // Leave room below Ninja's shared 15/minute/IP limit.
  var PRODUCT_REQUEST_LIMIT = 9; // Reserve three requests for changing cart totals.
  var RETRY_MS = 30000;

  function integer(value, minimum) {
    return typeof value === 'number' && Number.isSafeInteger(value) && value >= minimum;
  }

  function id(value) {
    if (typeof value === 'number' && !integer(value, 1)) return null;
    if (typeof value !== 'number' && typeof value !== 'string') return null;
    var text = String(value);
    return /^[1-9][0-9]*$/.test(text) ? text : null;
  }

  function product(value) {
    if (!value || !id(value.productId) || !id(value.variantId) ||
        !integer(value.quantity, 1) || !integer(value.price, 0) ||
        !Number.isSafeInteger(value.price * value.quantity)) return null;
    return { productId: id(value.productId), variantId: id(value.variantId),
      quantity: value.quantity, price: value.price };
  }

  function cartData(cart) {
    if (!cart || !Array.isArray(cart.items) || !integer(cart.items_subtotal_price, 0) ||
        !integer(cart.total_price, 0) || cart.total_price > cart.items_subtotal_price) return null;
    var items = [];
    var quantity = 0;
    var lineTotal = 0;
    var allocatedTotal = 0;
    var allocatedPrices = [];
    var completeAllocations = true;
    for (var i = 0; i < cart.items.length; i += 1) {
      var item = cart.items[i];
      if (!item || !id(item.product_id) || !id(item.variant_id) ||
          !integer(item.quantity, 1) || !integer(item.final_line_price, 0)) return null;
      quantity += item.quantity;
      lineTotal += item.final_line_price;
      if (!Number.isSafeInteger(quantity) || !Number.isSafeInteger(lineTotal)) return null;
      items.push({ productId: id(item.product_id), variantId: id(item.variant_id),
        quantity: item.quantity, price: item.final_line_price });
      var allocated = 0;
      var allocationsValid = integer(item.original_line_price, 0) && Array.isArray(item.discount_allocations);
      if (allocationsValid) {
        item.discount_allocations.forEach(function (allocation) {
          if (!allocation || !integer(allocation.amount, 0)) allocationsValid = false;
          else allocated += allocation.amount;
        });
      }
      var net = item.original_line_price - allocated;
      if (!allocationsValid || !integer(allocated, 0) || !integer(net, 0) || net > item.final_line_price) {
        completeAllocations = false;
      } else {
        allocatedPrices.push(net);
        allocatedTotal += net;
      }
    }
    if (cart.item_count !== undefined && cart.item_count !== quantity) return null;
    if (lineTotal !== cart.items_subtotal_price) return null;
    if (!items.length && (cart.items_subtotal_price !== 0 || cart.total_price !== 0)) return null;
    // Shopify order.subtotal_price includes all merchandise discounts. The Ajax
    // items subtotal does not include cart discounts; total_price does, without
    // checkout shipping/tax. Confirmed against Ninja's recorded Subtotal award.
    var result = { items: items, subtotal: cart.total_price, total: cart.total_price };
    if (cart.currency !== undefined) {
      if (typeof cart.currency !== 'string' || !/^[A-Z]{3}$/.test(cart.currency)) return null;
      result.currency = cart.currency;
    }
    if (completeAllocations && allocatedTotal === cart.total_price) {
      items.forEach(function (item, index) { item.price = allocatedPrices[index]; });
    } else if (lineTotal === cart.total_price) {
      // final_line_price is already exact when no cart discount remains.
    } else if (items.length === 1) {
      // With one line every merchandise discount belongs to that line.
      items[0].price = cart.total_price;
    } else {
      // Do not guess allocation across products with different bonus rules.
      result.reason = 'discount-allocation';
    }
    return result;
  }

  function unavailable(reason) {
    return Object.freeze({ count: null, status: 'unavailable', reason: reason });
  }

  function apiReason(status) {
    return typeof status === 'string' && /^[A-Za-z0-9-]{1,64}$/.test(status)
      ? 'api-' + status : 'api-status';
  }

  function result(data, status, product) {
    if (status !== 'Success') return unavailable(apiReason(status));
    if (!data || data.isExpired !== false) return unavailable(data && data.isExpired === true ? 'expired' : 'malformed-response');
    if (!integer(data.totalEntries, 0)) return unavailable('malformed-response');
    var ready = { count: data.totalEntries, status: 'ready' };
    // Ninja documents this as the active time multiplier; absence is unknown, not 1×.
    if (integer(data.multiplier, 1)) ready.multiplier = data.multiplier;
    // Show a breakdown only when the API rule fields fully reconcile with its total.
    // Unknown bundles, thresholds, caps or booster combinations keep the total alone.
    if (product && data.ruleType === 'quantity' && integer(data.ruleEntries, 1) &&
        integer(data.ruleQuantity, 1) && data.hasOrderRule === true &&
        data.rewardAmountSpent === 1 && integer(data.orderRuleEntries, 1) &&
        typeof data.orderRuleAmount === 'number' && data.orderRuleAmount > 0 &&
        integer(data.multiplier, 1) && data.aovMultiplier === 1) {
      var base = Math.floor(product.price * product.quantity / (data.orderRuleAmount * 100)) * data.orderRuleEntries;
      var bonus = Math.floor(product.quantity / data.ruleQuantity) * data.ruleEntries;
      if (integer(base, 0) && integer(bonus, 1) && (base + bonus) * data.multiplier === data.totalEntries) {
        ready.baseEntries = base;
        ready.bonusEntries = data.totalEntries - base;
      }
    }
    return Object.freeze(ready);
  }

  function create(options) {
    options = options || {};
    var fetcher = options.fetch || (root.fetch && root.fetch.bind(root));
    var now = options.now || Date.now;
    var ttl = integer(options.ttlMs, 1) ? options.ttlMs : 30000;
    var timeout = integer(options.timeoutMs, 1) ? options.timeoutMs : 8000;
    var giveawayId = typeof options.giveawayId === 'string' ? options.giveawayId.trim() : '';
    var apiKey = typeof options.publicApiKey === 'string' ? options.publicApiKey.trim() : '';
    var configured = Boolean(giveawayId && apiKey && typeof fetcher === 'function');
    var cache = new Map();
    var pending = new Map();
    var requestTimes = [];
    var blockedUntil = 0;

    function deadline(time, duration) {
      var until = time + duration;
      var boundary = typeof options.nextBoundary === 'function' ? options.nextBoundary(time) : null;
      return Number.isFinite(boundary) && boundary > time ? Math.min(until, boundary) : until;
    }

    function activeRequest(key) {
      var request = pending.get(key);
      if (request && (request.validUntil <= now() || now() < request.startedAt)) { pending.delete(key); return null; }
      return request;
    }

    function productKey(value) {
      var normalized = product(value);
      return normalized ? 'product:' + JSON.stringify(normalized) : null;
    }

    function cartKey(value) {
      var normalized = cartData(value);
      return normalized ? 'cart:' + JSON.stringify(normalized) : null;
    }

    function cached(key) {
      var entry = cache.get(key);
      if (!entry) return null;
      if (entry.until <= now() || now() < entry.startedAt) { cache.delete(key); return null; }
      cache.delete(key);
      cache.set(key, entry);
      return entry.value;
    }

    function remember(key, value, validUntil, startedAt) {
      // An old in-flight response must neither extend its lifetime nor evict a
      // newer quote already obtained after a campaign or multiplier boundary.
      if (validUntil <= now() || now() < startedAt || value.reason === 'stale-response') {
        return Object.freeze({ count: null, status: 'unavailable', reason: 'stale-response', validUntil: validUntil });
      }
      value = Object.freeze(Object.assign({}, value, { validUntil: validUntil }));
      cache.delete(key);
      cache.set(key, { value: value, until: validUntil, startedAt: startedAt });
      while (cache.size > CACHE_LIMIT) cache.delete(cache.keys().next().value);
      return value;
    }

    async function post(endpoint, data, validUntil) {
      if (!configured) return { failure: unavailable('not-configured') };
      var time = now();
      if (time < blockedUntil) return { failure: unavailable('cooldown') };
      var isOrder = endpoint === 'calculateOrderEntries';
      requestTimes = requestTimes.filter(function (request) { return time - request.time < 60000; });
      if (requestTimes.length >= REQUEST_LIMIT) return { failure: unavailable('rate-budget') };
      if (!isOrder && requestTimes.filter(function (request) { return !request.isOrder; }).length >= PRODUCT_REQUEST_LIMIT) {
        return { failure: unavailable('rate-budget') };
      }
      requestTimes.push({ time: time, isOrder: isOrder });
      var controller = typeof root.AbortController === 'function' ? new root.AbortController() : null;
      var timer;
      var failureReason = 'request-failed';
      try {
        var response = await Promise.race([
          Promise.resolve().then(function () {
            return fetcher(BASE + endpoint, {
              method: 'POST', mode: 'cors', credentials: 'omit', referrerPolicy: 'no-referrer',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(Object.assign({ giveawayId: giveawayId, apiKey: apiKey }, data)),
              signal: controller ? controller.signal : undefined
            });
          }).then(function (response) {
            if (!response || !response.ok) {
              if (response && integer(response.status, 100) && response.status <= 599) failureReason = 'http-' + response.status;
              throw new Error('http-error');
            }
            if (typeof response.json !== 'function') throw new Error('invalid-response');
            return response.json();
          }),
          new Promise(function (_, reject) {
            timer = root.setTimeout(function () {
              failureReason = 'timeout';
              reject(new Error('timeout'));
              if (controller) controller.abort();
            }, timeout);
          })
        ]);
        if (validUntil <= now()) return { failure: unavailable('stale-response') };
        if (!response || response.status !== 'Success') {
          blockedUntil = Math.max(blockedUntil, deadline(now(), RETRY_MS));
          return { failure: unavailable(apiReason(response && response.status)) };
        }
        return { payload: response };
      } catch (error) {
        if (validUntil <= now()) return { failure: unavailable('stale-response') };
        blockedUntil = Math.max(blockedUntil, deadline(now(), RETRY_MS));
        return { failure: unavailable(failureReason) };
      } finally {
        root.clearTimeout(timer);
      }
    }

    function startBatch(entries) {
      var startedAt = now();
      var validUntil = deadline(startedAt, ttl);
      var task = post('calculateEntriesBatch', { products: entries.map(function (entry) { return entry.value; }) }, validUntil);
      entries.forEach(function (entry, index) {
        var request = { startedAt: startedAt, validUntil: validUntil, promise: null };
        var itemTask = task.then(function (response) {
          if (response.failure) return response.failure;
          var rows = response.payload.data;
          var row = Array.isArray(rows) ? rows[index] : null;
          if (row && row.status !== 'Success') return unavailable(apiReason(row.status));
          // Ninja promises request order. IDs prevent applying another product's result.
          if (!row || id(row.productId) !== entry.value.productId || id(row.variantId) !== entry.value.variantId) {
            return unavailable('malformed-response');
          }
          return result(row, row.status, entry.value);
        }).then(function (value) {
          if (pending.get(entry.key) === request) pending.delete(entry.key);
          return remember(entry.key, value, validUntil, startedAt);
        });
        request.promise = itemTask;
        pending.set(entry.key, request);
      });
    }

    async function products(values) {
      if (!Array.isArray(values)) return new Map([[null, unavailable('invalid-input')]]);
      var requested = new Map();
      var fresh = [];
      values.forEach(function (value) {
        var normalized = product(value);
        var key = productKey(value);
        if (requested.has(key)) return;
        requested.set(key, null);
        if (!normalized) { requested.set(key, unavailable('invalid-input')); return; }
        var hit = cached(key);
        if (hit) requested.set(key, hit);
        else if (!activeRequest(key)) fresh.push({ key: key, value: normalized });
      });
      for (var start = 0; start < fresh.length; start += BATCH_LIMIT) startBatch(fresh.slice(start, start + BATCH_LIMIT));
      await Promise.all(Array.from(requested.keys()).map(async function (key) {
        if (!requested.get(key)) requested.set(key, await pending.get(key).promise);
      }));
      return requested;
    }

    async function order(cart) {
      var data = cartData(cart);
      if (!data) return unavailable('invalid-input');
      if (data.reason) return unavailable(data.reason);
      if (!data.items.length) return Object.freeze({ count: 0, status: 'ready' });
      var key = cartKey(cart);
      var hit = cached(key);
      if (hit) return hit;
      var existing = activeRequest(key);
      if (existing) return existing.promise;
      var startedAt = now();
      var validUntil = deadline(startedAt, ttl);
      var request = { startedAt: startedAt, validUntil: validUntil, promise: null };
      var task = post('calculateOrderEntries', { cartData: data }, validUntil).then(function (response) {
        return response.failure || result(response.payload.data, response.payload.status);
      }).then(function (value) {
        if (pending.get(key) === request) pending.delete(key);
        return remember(key, value, validUntil, startedAt);
      });
      request.promise = task;
      pending.set(key, request);
      return task;
    }

    return Object.freeze({ productKey: productKey, cartKey: cartKey, cartData: cartData, products: products, order: order });
  }

  return Object.freeze({ create: create });
});
